import { faMapPin } from "@fortawesome/pro-regular-svg-icons";
import { faDiamondTurnRight, faLocationArrow, faMap } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DialogContentText } from "@mui/material";
import React from "react";
import GlobalStoreContext from "../../../context/GlobalStoreContext";
import Helper from "../../../Helper";
import { Area } from "../../../models/Area";
import { CalendarEvent } from "../../../models/CalendarEvent";
import { MapObject } from "../../../models/MapObject";
import WaypointDirections from "../../Map/WaypointDirections";
import DamnMap from "../damnMap";
import DamnDirectionsModal from "./damnDirectionsModal";

interface DamnMapSectionProps {
    calendarEvent: CalendarEvent;
}

const DamnMapSection: React.FC<DamnMapSectionProps> = (props) => {
    const context = React.useContext(GlobalStoreContext);

    const project = context.projectInfo(props.calendarEvent.projectId);

    const [dropoff, setDropoff] = React.useState<Area | null>(null);
    const [waypoint, setWaypoint] = React.useState<Area | null>(null);
    const [mapObjects, setMapObjects] = React.useState<MapObject[]>([]);
    const [damnDirectionsModal, setDamnDirectionsModal] = React.useState<boolean>(false);

    const dropOffMapObject = React.useMemo(() => {
        if (!dropoff) return null;
        return dropoff.getMapObject(context);
    }, [context, dropoff]);

    const waypointMapObject = React.useMemo(() => {
        if (!waypoint) return null;
        return waypoint.getMapObject(context);
    }, [context, waypoint]);

    React.useEffect(() => {
        const d = context
            .mapAreasForProject(project?.id)
            .find((area) => area.id === props.calendarEvent.deliveryDropOffId());
        const w = context
            .mapAreasForProject(project?.id)
            .find((area) => area.id === props.calendarEvent.deliveryWaypointId());

        setDropoff(d || null);
        setWaypoint(w || null);

        setMapObjects(
            [d, w]
                .filter((obj) => obj !== undefined && obj !== null)
                .map((area) => area!.getMapObject(context))
                .filter((obj) => obj !== null)
        );
    }, [props.calendarEvent, context.mapAreasByProject, context.mapObjectsByProject]);

    const handleMapDirections = () => {
        if (waypointMapObject) {
            setDamnDirectionsModal(true);
            return;
        }
        if (dropOffMapObject) {
            Helper.openMapLink(dropOffMapObject);
        }
        return;
    };

    if (mapObjects.length === 0) {
        return null;
    }

    const verifyMapObjects = () => {
        if (!mapObjects || mapObjects.length === 0) {
            return false;
        }

        return true;
    };

    const deliveryDirectionsModalText = () => {
        if (waypointMapObject?.geometry == "lineString") {
            return (
                <>
                    <DialogContentText>
                        This delivery has a specified path in which to approach the dropoff location.
                    </DialogContentText>
                    <DialogContentText>
                        Click Get Directions below to navigate the start of the path and then return to this page for
                        waypoint guidance
                    </DialogContentText>
                </>
            );
        } else
            return (
                <>
                    <DialogContentText>
                        This delivery has a specified point in which to enter the jobsite.
                    </DialogContentText>
                    <DialogContentText>
                        Click Get Directions below to navigate the waypoint and then proceed to dropoff at{" "}
                        <div className="flex flex-row items-center font-semibold">
                            {Helper.areaAvatar(dropoff, dropoff?.getMapObject(context))} {dropoff?.areaName}
                        </div>
                    </DialogContentText>
                </>
            );
    };

    return (
        <section className="mx-4 mt-4">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200/50">
                <div className="p-5 pb-4 bg-linear-to-r from-gray-50 to-white">
                    <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="flex gap-2 items-center">
                            <FontAwesomeIcon icon={faMap} className="text-plot-orange" />
                            <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Navigation</h2>
                        </div>

                        <button
                            type="button"
                            onClick={handleMapDirections}
                            className="cursor-pointer bg-linear-to-r from-plot-orange to-plot-orange/90 hover:from-plot-orange/90 hover:to-plot-orange text-white font-bold p-3 md:p-4 rounded-xl flex items-center justify-center gap-3 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
                        >
                            <FontAwesomeIcon icon={faLocationArrow} />
                            Get Directions
                        </button>
                    </div>
                </div>

                {!context.waypointGuidance && verifyMapObjects() && (
                    <div className="relative w-full h-80 bg-linear-to-br from-gray-100 to-gray-200">
                        <DamnMap
                            mapId="damnMap"
                            mapObjects={mapObjects}
                            waypoint={waypoint}
                            geoControlRef={context.geoControlRef}
                            setUserLocation={context.setUserLocation}
                            project={project}
                        />
                    </div>
                )}

                {/* Navigation Info & Button */}
                <div className="p-5 flex flex-col gap-4">
                    {waypoint && (
                        <WaypointDirections
                            waypoint={waypoint}
                            dropoff={dropoff ? dropoff : undefined}
                            title={
                                <>
                                    <div className="flex items-center gap-3  bg-linear-to-r  rounded-xl ">
                                        <FontAwesomeIcon icon={faDiamondTurnRight} size="lg" />
                                        <div>
                                            <p className="text-xs text-gray-600 font-medium">Use Waypoint:</p>
                                            <div className="flex flex-row items-center text-base font-bold text-gray-900">
                                                {Helper.areaAvatar(waypoint, waypoint.getMapObject(context))}
                                                {waypoint?.areaName}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            }
                        />
                    )}
                    <div className="flex items-center gap-3 px-4 py-3 bg-linear-to-r from-plot-orange/5 to-plot-orange/10 rounded-xl ">
                        <FontAwesomeIcon icon={faMapPin} className=" text-green-500 shrink-0" size="lg" />
                        <div>
                            <p className="text-xs text-gray-600 font-medium">Drop off at:</p>
                            <div className="flex flex-row items-center text-base font-bold text-gray-900">
                                {Helper.areaAvatar(dropoff, dropOffMapObject)}
                                {dropoff?.areaName}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <DamnDirectionsModal
                open={damnDirectionsModal}
                toggleOpen={() => setDamnDirectionsModal(!damnDirectionsModal)}
                handleMapDirections={() => Helper.openMapLink(waypointMapObject!)}
                body={deliveryDirectionsModalText()}
            />
        </section>
    );
};

export default DamnMapSection;
